<template>
    <div class="d-flex flex-column" style="min-height: 0">
        <v-container
            class="flex-grow-1 d-flex flex-column pa-0"
            style="min-height: 0"
        >
            <div
                v-if="
                    this.$route.name === 'UserSettings' && this.user != null
                "
                class="d-flex flex-column align-center flex-shrink-0 pa-3"
            >
                <v-avatar size="50">
                    <v-icon size="50" color="primary"
                        >mdi-account-circle</v-icon
                    >
                </v-avatar>
                <h3>
                    {{ this.user.name }} {{ this.user.surname }} ({{
                        this.user.role
                    }})
                </h3>
            </div>
            <v-card
                elevation="4"
                class="d-flex flex-column flex-grow-1"
                style="min-height: 0"
            >
                <v-toolbar color="#F5F5F5">
                    <v-toolbar-title class="pl-2">
                        Edit account details
                    </v-toolbar-title>
                </v-toolbar>
                <v-card-text
                    class="pl-7 pr-7 flex-grow-1"
                    style="min-height: 0; overflow-y: auto; overflow-x: hidden;"
                >
                    <v-form class="d-flex flex-column mt-6" width="100%">
                        <v-row>
                            <!-- Name -->
                            <v-col cols="12" sm="6">
                                <v-text-field
                                    hide-details
                                    density="compact"
                                    variant="outlined"
                                    label="Name"
                                    v-model="form.name"
                                    @input="onFormChanged"
                                />
                            </v-col>

                            <!-- Surname -->
                            <v-col cols="12" sm="6">
                                <v-text-field
                                    hide-details
                                    density="compact"
                                    variant="outlined"
                                    label="Surname"
                                    v-model="form.surname"
                                    @input="onFormChanged"
                                />
                            </v-col>

                            <!-- Phone -->
                            <v-col cols="12" sm="6">
                                <v-text-field
                                    hide-details
                                    density="compact"
                                    variant="outlined"
                                    label="Phone number"
                                    v-model="form.phoneNumber"
                                    @input="onFormChanged"
                                />
                            </v-col>

                            <!-- Account Status -->
                            <v-col cols="12" sm="6" v-if="showAccountStatus">
                                <v-select
                                    hide-details
                                    density="compact"
                                    variant="outlined"
                                    label="Account status"
                                    :items="['Active', 'Inactive']"
                                    v-model="selectedAccountStatus"
                                    :disabled="!canChangeAccountStatus"
                                    @update:modelValue="
                                        handleAccountStatusChange
                                    "
                                />
                            </v-col>

                            <!-- User role: editable for operator/teamLead; read-only Admin for admins -->
                            <v-col cols="12" sm="6" v-if="showUserRoleField">
                                <v-select
                                    hide-details
                                    density="compact"
                                    variant="outlined"
                                    label="User role"
                                    :items="displayedUserRoleOptions"
                                    item-title="title"
                                    item-value="value"
                                    v-model="selectedUserRole"
                                    :disabled="!canChangeUserRole"
                                    @update:modelValue="handleUserRoleChange"
                                />
                            </v-col>
                        </v-row>

                        <v-dialog
                            v-model="showSoleTeamLeadDialog"
                            max-width="520"
                            persistent
                        >
                            <v-card>
                                <v-card-title class="text-wrap">
                                    Cannot change role
                                </v-card-title>
                                <v-card-text>
                                    <p class="mb-3">
                                        User {{ demoteUserDisplayName }} is
                                        currently the only Team Lead on the
                                        following projects:
                                    </p>
                                    <ul class="mb-3 pl-4">
                                        <li
                                            v-for="p in soleTeamLeadProjects"
                                            :key="p.projectId"
                                        >
                                            {{ p.name }}
                                        </li>
                                    </ul>
                                    <p class="mb-0">
                                        Please add another Team Lead to the
                                        projects so you can change
                                        {{ demoteUserDisplayName }}'s role to
                                        operator.
                                    </p>
                                </v-card-text>
                                <v-card-actions>
                                    <v-spacer />
                                    <v-btn
                                        color="primary"
                                        variant="flat"
                                        @click="closeSoleTeamLeadDialog"
                                    >
                                        OK
                                    </v-btn>
                                </v-card-actions>
                            </v-card>
                        </v-dialog>
                        <!-- Projects Section (operator, team lead, and admin) -->
                        <v-row v-if="shouldShowProjects" class="pl-4 mb-4">
                            <v-col cols="12" class="pa-0">
                                <VChipProjectsList
                                    :projects="associatedProjects"
                                    title="Associated projects"
                                />
                                <VChipProjectsList
                                    v-if="showTeamLeadOnProjects"
                                    :projects="teamLeadOnProjects"
                                    title="Team Lead on"
                                />
                            </v-col>
                        </v-row>
                        <!-- Out of Office -->
                        <v-row class="pl-4 mt-0">
                            <v-switch
                                v-model="outOfOffice"
                                @change="handleOutOfOfficeToggle"
                                color="primary"
                                hide-details
                            >
                                <template v-slot:label>
                                    <span
                                        :class="[
                                            outOfOffice
                                                ? 'text-primary opacity-100'
                                                : 'text-grey opacity-100',
                                            'font-weight-bold',
                                        ]"
                                    >
                                        Out of office
                                    </span>
                                </template>
                            </v-switch>

                            <v-overlay v-model="showOverlay" opacity="0.5">
                                <v-snackbar
                                    color="#333"
                                    height="78px"
                                    v-model="snackbar"
                                    class="mb-16"
                                    rounded="shaped"
                                    :timeout="5000"
                                    top
                                    right
                                >
                                    {{ snackbarMessage }}
                                    <template v-slot:actions>
                                        <v-btn
                                            color="error"
                                            variant="text"
                                            @click="confirmAction"
                                            >OK</v-btn
                                        >
                                    </template>
                                </v-snackbar>
                            </v-overlay>
                        </v-row>

                        <!-- App bar animation preference (self only — personal UI pref) -->
                        <v-row v-if="isSelf" class="pl-4 mt-0">
                            <v-switch
                                :model-value="appBarAnimationEnabled"
                                color="primary"
                                hide-details
                                @update:modelValue="handleAppBarAnimationToggle"
                            >
                                <template v-slot:label>
                                    <span
                                        :class="[
                                            appBarAnimationEnabled
                                                ? 'text-primary opacity-100'
                                                : 'text-grey opacity-100',
                                            'font-weight-bold',
                                        ]"
                                    >
                                        {{
                                            appBarAnimationEnabled
                                                ? "Turn OFF Animation"
                                                : "Turn ON Animation"
                                        }}
                                    </span>
                                </template>
                            </v-switch>
                        </v-row>
                    </v-form></v-card-text
                >
            </v-card>
        </v-container>
        <BottomButtonWrapper :is-showing="isFormChanged">
            <v-col class="d-flex justify-center" cols="6">
                <ThemedActionButton
                    :buttonColor="'error'"
                    :text="'CANCEL'"
                    @click="resetForm"
                />
            </v-col>
            <v-col class="d-flex justify-center" cols="6">
                <ThemedActionButton
                    :buttonColor="'success'"
                    :text="'SAVE'"
                    @click="saveChanges"
                />
            </v-col>
        </BottomButtonWrapper>
    </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { db, auth } from "@/firebaseConfig";
import { ref as rtdbRef, update } from "firebase/database";
import BottomButtonWrapper from "@/components/BottomButtonWrapper.vue";
import ThemedActionButton from "@/components/ThemedActionButton.vue";
import { getActiveEntry } from "@/utils/utils";
import {
    getSoleTeamLeadProjects,
    isProjectTeamLead,
} from "@/utils/projectUtils";
import VChipProjectsList from "@/components/VChipProjectsList.vue";

export default {
    name: "UserProfileForm",
    components: { BottomButtonWrapper, ThemedActionButton, VChipProjectsList },

    props: {
        /**
         * If provided, edits THAT user. If omitted, edits the authenticated user (self).
         */
        userId: { type: String, default: null },
        /**
         * Render the Active/Inactive selector.
         */
        showAccountStatus: { type: Boolean, default: false },
        /**
         * Enable interaction with the account status selector.
         */
        canChangeAccountStatus: { type: Boolean, default: false },
        /**
         * Use the overlay confirmation before switching to OOO while an entry is open.
         */
        useOverlayConfirmForOOO: { type: Boolean, default: true },
    },

    data() {
        return {
            form: { name: "", surname: "", phoneNumber: "" },
            outOfOffice: false,
            appBarAnimationEnabled: false,
            selectedAccountStatus: "Active",
            selectedUserRole: "operator",
            userRoleOptions: [
                { title: "Operator", value: "operator" },
                { title: "Team Lead", value: "teamLead" },
            ],
            isFormChanged: false,
            showSoleTeamLeadDialog: false,
            soleTeamLeadProjects: [],

            // overlay/snackbar
            showOverlay: false,
            snackbar: false,
            snackbarMessage: "",
            snackbarAction: null,
        };
    },
    async created() {
        if (
            !this.$store.getters.getAllProjects ||
            this.$store.getters.getAllProjects.length === 0
        ) {
            await this.$store.dispatch("fetchProjects");
        }
    },
    computed: {
        ...mapGetters({
            user: "getUser",
            users: "getUsers",
            getRights: "getRights",
        }),

        /**
         * Get the role of the target user.
         * Uses existing targetUser computed property.
         */
        targetUserRole() {
            return this.targetUser?.role || null;
        },
        canChangeUserRole() {
            if (this.isSelf) return false;
            const role = this.targetUserRole;
            if (role !== "operator" && role !== "teamLead") return false;
            // Admins only (viewUsers "all"); team leads cannot promote/demote.
            return this.getRights?.viewUsers === "all";
        },
        /**
         * Show role for everyone who can open the profile; only admins can edit
         * (see canChangeUserRole). Team leads see it disabled.
         */
        showUserRoleField() {
            if (!this.targetUser) return false;
            const role = this.targetUserRole;
            return (
                role === "operator" ||
                role === "teamLead" ||
                role === "admin"
            );
        },
        displayedUserRoleOptions() {
            if (this.targetUserRole === "admin") {
                return [{ title: "Admin", value: "admin" }];
            }
            return this.userRoleOptions;
        },
        demoteUserDisplayName() {
            const u = this.targetUser;
            if (!u) return "User";
            const name = `${u.name || ""} ${u.surname || ""}`.trim();
            return name || u.email || "User";
        },
        /**
         * Get projects assigned to the target user.
         * Uses getProjectsForUser which gets data directly from user.projects.
         * Filters out system projects.
         */
        associatedProjects() {
            // Use getProjectsForUser which returns projects from user.projects directly
            const userProjects =
                this.$store.getters.getProjectsForUser(this.effectiveUserId) ||
                [];

            // Filter out system projects (like SYSTEM_LUNCH-BREAK)
            return userProjects
                .filter((project) => project.status !== "systemProject")
                .map((project) => ({
                    projectId: project.projectId,
                    name: project.name || "Unnamed Project",
                }));
        },
        /**
         * Projects where the target user is listed in teamLeaders.
         * Shown for team leads and admins (admins may lead projects too).
         */
        teamLeadOnProjects() {
            if (!this.showTeamLeadOnProjects || !this.effectiveUserId) {
                return [];
            }

            const allProjects = this.$store.getters.getAllProjects || [];
            if (!allProjects.length) return [];

            return allProjects
                .filter((project) =>
                    isProjectTeamLead(project, this.effectiveUserId)
                )
                .map((project) => ({
                    projectId: project.projectId,
                    name: project.name || "Unnamed Project",
                }));
        },
        showTeamLeadOnProjects() {
            const role = this.targetUserRole;
            return role === "teamLead" || role === "admin";
        },

        /**
         * Projects section for operator, team lead, and admin when they have lists to show.
         */
        shouldShowProjects() {
            if (!this.targetUser) return false;
            const role = this.targetUserRole;
            if (
                role !== "operator" &&
                role !== "teamLead" &&
                role !== "admin"
            ) {
                return false;
            }
            return (
                this.associatedProjects.length > 0 ||
                (this.showTeamLeadOnProjects &&
                    this.teamLeadOnProjects.length > 0)
            );
        },

        effectiveUserId() {
            return this.userId || auth.currentUser?.uid || null;
        },

        /**
         * Whether we're editing the current authenticated user (self).
         * Derived from effectiveUserId for consistency.
         */
        isSelf() {
            return this.effectiveUserId === auth.currentUser?.uid;
        },

        targetUser() {
            if (!this.effectiveUserId) return null;
            const allUsers = this.users;
            if (allUsers) {
                const list = Array.isArray(allUsers)
                    ? allUsers
                    : Object.values(allUsers);
                const found = list.find((u) => u?.uid === this.effectiveUserId);
                if (found) return found;
            }
            if (this.isSelf) return this.user;
            return null;
        },

        activeEntry() {
            return getActiveEntry(this.targetUser);
        },
    },

    watch: {
        targetUser: {
            immediate: true,
            handler(u) {
                if (!u) return;
                this.form.name = u.name || "";
                this.form.surname = u.surname || "";
                this.form.phoneNumber = u.phoneNumber || "";
                this.outOfOffice = u.userStatus === "outOfOffice";
                this.appBarAnimationEnabled = !!u.appBarAnimation;
                const displayAcc = u.accountStatus || "active";
                this.selectedAccountStatus =
                    displayAcc.charAt(0).toUpperCase() + displayAcc.slice(1);
                if (
                    u.role === "teamLead" ||
                    u.role === "operator" ||
                    u.role === "admin"
                ) {
                    this.selectedUserRole = u.role;
                }
                this.isFormChanged = false;
            },
        },
    },

    methods: {
        ...mapActions([
            "endCurrentTimeEntry", // (self) { isEndOfDay, activeEntry, userId }
            "endTimeEntryForUser", // (other) { userId, entryId, isEndOfDay }
            "endCurrentProject", // (self) true/false
            "updateUserStatus", // (self) status
            "setUser", // set local vuex user
            "updateAccountStatus", // { userId, status }
            "updateUserRole",
        ]),

        onFormChanged() {
            this.isFormChanged = this.hasPendingFormChanges();
        },

        /**
         * Compare editable fields to the persisted target user.
         * App-bar animation is saved immediately on its own toggle (self only).
         */
        hasPendingFormChanges() {
            const u = this.targetUser;
            if (!u) return false;
            if ((this.form.name || "") !== (u.name || "")) return true;
            if ((this.form.surname || "") !== (u.surname || "")) return true;
            if ((this.form.phoneNumber || "") !== (u.phoneNumber || "")) {
                return true;
            }
            if (
                this.canChangeUserRole &&
                this.selectedUserRole &&
                this.selectedUserRole !== u.role
            ) {
                return true;
            }
            return false;
        },

        buildProfileUpdates() {
            const u = this.targetUser;
            if (!u) return {};
            const updates = {};
            if ((this.form.name || "") !== (u.name || "")) {
                updates.name = this.form.name;
            }
            if ((this.form.surname || "") !== (u.surname || "")) {
                updates.surname = this.form.surname;
            }
            if ((this.form.phoneNumber || "") !== (u.phoneNumber || "")) {
                updates.phoneNumber = this.form.phoneNumber;
            }
            return updates;
        },

        getSoleTeamLeadBlockers() {
            const allProjects = this.$store.getters.getAllProjects || [];
            return getSoleTeamLeadProjects(
                allProjects,
                this.effectiveUserId
            ).map((p) => ({
                projectId: p.projectId,
                name: p.name || "Unnamed Project",
            }));
        },

        async handleAppBarAnimationToggle(val) {
            if (!this.isSelf || !this.effectiveUserId) return;

            const next = !!val;
            const prev = this.appBarAnimationEnabled;

            this.appBarAnimationEnabled = next;

            try {
                await update(rtdbRef(db, `users/${this.effectiveUserId}`), {
                    appBarAnimation: next,
                });

                const currentUser = this.$store.state.user;
                if (currentUser) {
                    this.setUser({ ...currentUser, appBarAnimation: next });
                }
            } catch (e) {
                console.error(
                    "[UserProfileForm] Failed to update animation preference:",
                    e
                );
                this.appBarAnimationEnabled = prev;
            }
        },
        async removeUserFromAllActiveProjects(userId) {
            const allProjects = [
                ...(this.$store.state.managedProjects || []),
                ...(this.$store.state.workingProjects || []),
            ];

            for (const project of allProjects) {
                if (project?.activeUsers && project.activeUsers[userId]) {
                    await this.$store.dispatch("addRemoveActiveUser", {
                        oldProject: { projectId: project.projectId },
                        newProject: null, // null → remove from activeUsers
                        userId,
                        name: null, // keep same shape as in your other code
                        surname: null,
                    });
                }
            }
        },
        triggerSnackbar(message, action) {
            this.showOverlay = true;
            this.snackbarMessage = message;
            this.snackbarAction = action || null;
            this.snackbar = true;
        },

        confirmAction() {
            if (this.snackbarAction) this.snackbarAction();
            this.showOverlay = false;
            this.snackbar = false;
        },

        async handleOutOfOfficeToggle() {
            if (!this.targetUser || !this.effectiveUserId) return;

            const hasActive = !!this.activeEntry;
            const goingOOO = this.outOfOffice === true;

            // Only confirm when turning OOO ON and there is an active entry
            if (goingOOO && hasActive) {
                this.triggerSnackbar(
                    "User is currently working on a project. End it and set status to 'Out of Office'?",
                    async () => {
                        try {
                            // 1) End open entry
                            if (this.isSelf) {
                                await this.endCurrentTimeEntry({
                                    isEndOfDay: true,
                                    activeEntry: this.activeEntry,
                                    userId: this.effectiveUserId,
                                });
                                await this.endCurrentProject(true);
                            } else {
                                await this.endTimeEntryForUser({
                                    userId: this.effectiveUserId,
                                    entryId: this.activeEntry.uid,
                                    isEndOfDay: true,
                                });
                            }

                            // 2) Remove user from all projects' activeUsers
                            await this.removeUserFromAllActiveProjects(
                                this.effectiveUserId
                            );

                            // 3) Persist status: outOfOffice
                            this.outOfOffice = true; // keep UI in sync after confirm
                            await this.changeStatus();
                        } catch (e) {
                            console.error(
                                "[UserProfileForm] OOO toggle failed:",
                                e
                            );
                        } finally {
                            this.snackbar = false;
                        }
                    }
                );

                // Flip back until confirmed
                this.outOfOffice = false;
                return;
            }

            // No confirmation needed (either turning OOO off, or turning it on with no active entry)
            await this.changeStatus();
        },

        async changeStatus() {
            const newStatus = this.outOfOffice ? "outOfOffice" : "loggedIn";
            try {
                if (this.isSelf) {
                    await this.updateUserStatus(newStatus);
                } else {
                    await update(rtdbRef(db, `users/${this.effectiveUserId}`), {
                        userStatus: newStatus,
                    });
                }
            } catch (e) {
                console.error("[UserProfileForm] changeStatus failed:", e);
            }
        },

        async saveChanges() {
            if (!this.effectiveUserId || !this.targetUser) {
                console.error("[UserProfileForm] No effectiveUserId.");
                return;
            }

            const updates = this.buildProfileUpdates();
            const currentRole = this.targetUser.role;
            const roleChanged =
                this.canChangeUserRole &&
                this.selectedUserRole &&
                this.selectedUserRole !== currentRole &&
                (this.selectedUserRole === "operator" ||
                    this.selectedUserRole === "teamLead");

            if (!Object.keys(updates).length && !roleChanged) {
                this.isFormChanged = false;
                return;
            }

            try {
                if (roleChanged && this.selectedUserRole === "operator") {
                    const sole = this.getSoleTeamLeadBlockers();
                    if (sole.length) {
                        this.soleTeamLeadProjects = sole;
                        this.showSoleTeamLeadDialog = true;
                        this.selectedUserRole = currentRole;
                        this.isFormChanged = this.hasPendingFormChanges();
                        return;
                    }
                }

                if (Object.keys(updates).length) {
                    await update(
                        rtdbRef(db, `users/${this.effectiveUserId}`),
                        updates
                    );
                }

                if (roleChanged) {
                    await this.updateUserRole({
                        userId: this.effectiveUserId,
                        role: this.selectedUserRole,
                    });
                }

                // Only update store when editing self. When admin/TL saves another user we only persist to DB.
                if (this.isSelf && Object.keys(updates).length) {
                    const currentUser = this.$store.state.user;
                    if (currentUser) {
                        this.setUser({
                            ...currentUser,
                            ...updates,
                        });
                    }
                }

                this.isFormChanged = false;
                this.$emit("saved", { userId: this.effectiveUserId });
            } catch (e) {
                console.error("[UserProfileForm] Error saving changes:", e);
            }
        },

        resetForm() {
            if (!this.targetUser) return;
            this.form.name = this.targetUser.name || "";
            this.form.surname = this.targetUser.surname || "";
            this.form.phoneNumber = this.targetUser.phoneNumber || "";
            this.appBarAnimationEnabled = !!this.targetUser.appBarAnimation;
            const role = this.targetUser.role;
            if (
                role === "teamLead" ||
                role === "operator" ||
                role === "admin"
            ) {
                this.selectedUserRole = role;
            }
            this.isFormChanged = false;
        },

        async handleAccountStatusChange(newValue) {
            // normalize values
            const newStatus = (newValue || "").toLowerCase();

            // IMPORTANT: read the status of the *target* user (not always the signed-in user)
            const currentStatus = (
                this.targetUser?.accountStatus || "active"
            ).toLowerCase();
            if (!newStatus || newStatus === currentStatus) return;

            // IMPORTANT: write to the *target* user's node
            const targetUserId = this.effectiveUserId;
            if (!targetUserId) return;

            try {
                // Persist only the accountStatus.
                // The target client’s MainLayout watcher will observe the change and call logout() there.
                await this.$store.dispatch("updateAccountStatus", {
                    userId: targetUserId,
                    status: newStatus,
                });

                // Reflect selection in this form’s UI
                this.selectedAccountStatus = newValue;
            } catch (error) {
                console.error("Error changing account status:", error);
                // revert UI selector to currently stored value
                const current = this.targetUser?.accountStatus || "Active";
                this.selectedAccountStatus =
                    current.charAt(0).toUpperCase() + current.slice(1);
            }
        },

        closeSoleTeamLeadDialog() {
            this.showSoleTeamLeadDialog = false;
            this.soleTeamLeadProjects = [];
            const role = this.targetUser?.role;
            if (role === "teamLead" || role === "operator") {
                this.selectedUserRole = role;
            }
            this.isFormChanged = this.hasPendingFormChanges();
        },

        handleUserRoleChange(newRole) {
            const currentRole = this.targetUser?.role;
            if (!this.canChangeUserRole || !this.effectiveUserId) {
                this.selectedUserRole = currentRole;
                this.isFormChanged = this.hasPendingFormChanges();
                return;
            }

            // Selecting back to the saved role just clears that pending change.
            if (!newRole || newRole === currentRole) {
                this.selectedUserRole = currentRole;
                this.isFormChanged = this.hasPendingFormChanges();
                return;
            }

            // Validate demotion early; persist only on Save.
            if (newRole === "operator") {
                const sole = this.getSoleTeamLeadBlockers();
                if (sole.length) {
                    this.soleTeamLeadProjects = sole;
                    this.showSoleTeamLeadDialog = true;
                    this.selectedUserRole = currentRole;
                    this.isFormChanged = this.hasPendingFormChanges();
                    return;
                }
            }

            this.selectedUserRole = newRole;
            this.isFormChanged = this.hasPendingFormChanges();
        },
    },
};
</script>